import { CORE_SIZE } from './constants.es.js';
import colorName from 'color-name';

const float = '-?\\d*(?:\\.\\d+)';
const number = `(${float}?)`;
const percentage = `(${float}?%)`;
const numberOrPercentage = `(${float}?%?)`;
const pattern1 = /^#([a-f0-9]{3,4}|[a-f0-9]{4}(?:[a-f0-9]{2}){1,2})\b$/;
const hexRe = new RegExp(pattern1, 'i');
const pattern2 = `^
  hsla?\\(
    \\s*(-?\\d*(?:\\.\\d+)?(?:deg|rad|turn)?)\\s*,
    \\s*${percentage}\\s*,
    \\s*${percentage}\\s*
    (?:,\\s*${numberOrPercentage}\\s*)?
  \\)
  $
`.replace(/\n|\s/g, '');
const hsl3Re = new RegExp(pattern2);
const pattern3 = `^
  hsla?\\(
    \\s*(-?\\d*(?:\\.\\d+)?(?:deg|rad|turn)?)\\s*
    \\s+${percentage}
    \\s+${percentage}
    \\s*(?:\\s*\\/\\s*${numberOrPercentage}\\s*)?
  \\)
  $
`.replace(/\n|\s/g, '');
const hsl4Re = new RegExp(pattern3);
const pattern4 = `^
  rgba?\\(
    \\s*${number}\\s*,
    \\s*${number}\\s*,
    \\s*${number}\\s*
    (?:,\\s*${numberOrPercentage}\\s*)?
  \\)
  $
`.replace(/\n|\s/g, '');
const rgb3NumberRe = new RegExp(pattern4);
const pattern5 = `^
  rgba?\\(
    \\s*${percentage}\\s*,
    \\s*${percentage}\\s*,
    \\s*${percentage}\\s*
    (?:,\\s*${numberOrPercentage}\\s*)?
  \\)
  $
`.replace(/\n|\s/g, '');
const rgb3PercentageRe = new RegExp(pattern5);
const pattern6 = `^
  rgba?\\(
    \\s*${number}
    \\s+${number}
    \\s+${number}
    \\s*(?:\\s*\\/\\s*${numberOrPercentage}\\s*)?
  \\)
$
`.replace(/\n|\s/g, '');
const rgb4NumberRe = new RegExp(pattern6);
const pattern7 = `^
  rgba?\\(
    \\s*${percentage}
    \\s+${percentage}
    \\s+${percentage}
    \\s*(?:\\s*\\/\\s*${numberOrPercentage}\\s*)?
  \\)
$
`.replace(/\n|\s/g, '');
const rgb4PercentageRe = new RegExp(pattern7);
const pattern8 = /^transparent$/;
const transparentRe = new RegExp(pattern8, 'i');

const hexCharacters = 'a-f\\d';
const match3or4Hex = `#?[${hexCharacters}]{3}[${hexCharacters}]?`;
const match6or8Hex = `#?[${hexCharacters}]{6}([${hexCharacters}]{2})?`;
const nonHexChars = new RegExp(`[^#${hexCharacters}]`, 'gi');
const validHexSize = new RegExp(`^${match3or4Hex}$|^${match6or8Hex}$`, 'i');
function hex2Rgb(hex, options = { format: undefined }) {
    if (typeof hex !== 'string' ||
        nonHexChars.test(hex) ||
        !validHexSize.test(hex)) {
        throw new TypeError('Expected a valid hex string');
    }
    hex = hex.replace(/^#/, '');
    let alpha = 1;
    if (hex.length === 8) {
        alpha = Number.parseInt(hex.slice(6, 8), 16) / 255;
        hex = hex.slice(0, 6);
    }
    if (hex.length === 4) {
        alpha = Number.parseInt(hex.slice(3, 4).repeat(2), 16) / 255;
        hex = hex.slice(0, 3);
    }
    if (hex.length === 3) {
        hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    const number = Number.parseInt(hex, 16);
    const red = number >> 16;
    const green = (number >> 8) & 255;
    const blue = number & 255;
    if (options.format === 'array') {
        return [red, green, blue, alpha];
    }
    if (options.format === 'css') {
        const alphaString = alpha === 1 ? '' : ` / ${Number((alpha * 100).toFixed(2))}%`;
        return `rgb(${red} ${green} ${blue}${alphaString})`;
    }
    return { red, green, blue, alpha };
}

const clamp$1 = (num, min, max) => Math.min(Math.max(min, num), max);
const parseRGB = (num) => {
    let n = num;
    if (typeof n !== 'number')
        n = n.endsWith('%') ? (parseFloat(n) * 255) / 100 : parseFloat(n);
    return clamp$1(Math.round(n), 0, 255);
};
const parsePercentage = (percentage) => clamp$1(parseFloat(percentage), 0, 100);
function parseAlpha(alpha) {
    let a = alpha;
    if (typeof a !== 'number')
        a = a.endsWith('%') ? parseFloat(a) / 100 : parseFloat(a);
    return clamp$1(a, 0, 1);
}
function getHEX(hex) {
    const [r, g, b, a] = hex2Rgb(hex, { format: 'array' });
    return getRGB([null, ...[r, g, b, a]]);
}
function getHSL([, h, s, l, a = 1]) {
    let hh = h;
    if (hh.endsWith('turn')) {
        hh = (parseFloat(hh) * 360) / 1;
    }
    else if (hh.endsWith('rad')) {
        hh = Math.round((parseFloat(hh) * 180) / Math.PI);
    }
    else {
        hh = parseFloat(hh);
    }
    return {
        type: 'hsl',
        values: [hh, parsePercentage(s), parsePercentage(l)],
        alpha: parseAlpha(a === null ? 1 : a),
    };
}
function getRGB([, r, g, b, a = 1]) {
    return {
        type: 'rgb',
        values: [r, g, b].map(parseRGB),
        alpha: parseAlpha(a === null ? 1 : a),
    };
}

const parseCSSColor = (str) => {
    if (typeof str !== 'string')
        return null;
    const hex = hexRe.exec(str);
    if (hex)
        return getHEX(hex[0]);
    const hsl = hsl4Re.exec(str) || hsl3Re.exec(str);
    if (hsl)
        return getHSL(hsl);
    const rgb = rgb4NumberRe.exec(str) ||
        rgb4PercentageRe.exec(str) ||
        rgb3NumberRe.exec(str) ||
        rgb3PercentageRe.exec(str);
    if (rgb)
        return getRGB(rgb);
    if (transparentRe.exec(str))
        return getRGB([null, 0, 0, 0, 0]);
    const cn = colorName[str.toLowerCase()];
    if (cn)
        return getRGB([null, cn[0], cn[1], cn[2], 1]);
    return null;
};
var parse = parseCSSColor;

function clamp(val, min, max) {
    return Math.min(Math.max(val, min), max);
}
function hsl2rgb(hsl) {
    const h = hsl[0] / 360, s = hsl[1] / 100, l = hsl[2] / 100;
    let t2, t3, val;
    if (s == 0) {
        val = l * 255;
        return [val, val, val];
    }
    if (l < 0.5)
        t2 = l * (1 + s);
    else
        t2 = l + s - l * s;
    const t1 = 2 * l - t2;
    const rgb = [0, 0, 0];
    for (let i = 0; i < 3; i++) {
        t3 = h + (1 / 3) * -(i - 1);
        t3 < 0 && t3++;
        t3 > 1 && t3--;
        if (6 * t3 < 1)
            val = t1 + (t2 - t1) * 6 * t3;
        else if (2 * t3 < 1)
            val = t2;
        else if (3 * t3 < 2)
            val = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
        else
            val = t1;
        rgb[i] = val * 255;
    }
    return rgb;
}
function componentToHex(c) {
    const value = Math.round(clamp(c, 0, 255));
    const hex = value.toString(16);
    return hex.length == 1 ? '0' + hex : hex;
}
function rgb2hex(rgb) {
    const alpha = rgb.length === 4 ? componentToHex(rgb[3] * 255) : '';
    return ('#' +
        componentToHex(rgb[0]) +
        componentToHex(rgb[1]) +
        componentToHex(rgb[2]) +
        alpha);
}
function rgb2hsl(rgb) {
    const r = rgb[0] / 255, g = rgb[1] / 255, b = rgb[2] / 255, min = Math.min(r, g, b), max = Math.max(r, g, b), delta = max - min;
    let h, s;
    if (max == min)
        h = 0;
    else if (r == max)
        h = (g - b) / delta;
    else if (g == max)
        h = 2 + (b - r) / delta;
    else if (b == max)
        h = 4 + (r - g) / delta;
    h = Math.min(h * 60, 360);
    if (h < 0)
        h += 360;
    const l = (min + max) / 2;
    if (max == min)
        s = 0;
    else if (l <= 0.5)
        s = delta / (max + min);
    else
        s = delta / (2 - max - min);
    return [h, s * 100, l * 100];
}

function parseColor(color) {
    const res = parse(color);
    if (res === null)
        return null;
    if (res.type === 'hsl')
        res.values = hsl2rgb(res.values);
    return res;
}
function mix(color1, color2, percentage = 50) {
    const c1 = parseColor(color1);
    const c2 = parseColor(color2);
    if (!c1 || !c2)
        return null;
    const p = Math.min(Math.max(0, percentage), 100) / 100.0;
    const w = p * 2 - 1;
    const a = c1.alpha - c2.alpha;
    const w1 = ((w * a === -1 ? w : (w + a) / (1 + w * a)) + 1) / 2.0;
    const w2 = 1 - w1;
    const [r, g, b] = c1.values.map((c, i) => Math.round(c1.values[i] * w1 + c2.values[i] * w2));
    const alpha = parseFloat((c1.alpha * p + c2.alpha * (1 - p)).toFixed(8));
    return {
        hex: rgb2hex([r, g, b]),
        hexa: rgb2hex([r, g, b, alpha]),
        rgba: [r, g, b, alpha],
        hsla: [...rgb2hsl([r, g, b]).map(Math.round), alpha],
    };
}

const defaultNumberParam = (v, d) => v === null || isNaN(v) || typeof v === 'string' ? d : v;
class ColorCore {
    constructor(color = '#000', type = 'base', weight = 0) {
        [this.rgb, this.alpha, this.type, this.weight] = [
            [0, 0, 0],
            1,
            type,
            weight,
        ];
        const c = color === null ? '#000' : color;
        if (typeof c !== 'string')
            throw new TypeError(`Input should be a string: ${c}`);
        const parsed = parse(c);
        if (!parsed)
            throw new Error(`Unable to parse color from string: ${c}`);
        return this[`_setFrom${parsed.type.toUpperCase()}`]([
            ...parsed.values,
            parsed.alpha,
        ]);
    }
    get hex() {
        return this.hexString().replace(/^#/, '');
    }
    setColor(color) {
        const parsed = parse(color);
        if (!parsed)
            return null;
        return this[`_setFrom${parsed.type.toUpperCase()}`]([
            ...parsed.values,
            parsed.alpha,
        ]);
    }
    tint(weight, w = defaultNumberParam(weight, 50)) {
        return new ColorCore(`rgb(${mix('#fff', this.rgbString(), w).rgba})`, 'tint', w);
    }
    shade(weight, w = defaultNumberParam(weight, 50)) {
        return new ColorCore(`rgb(${mix('#000', this.rgbString(), w).rgba})`, 'shade', w);
    }
    tints(weight, w = defaultNumberParam(weight, 10)) {
        return Array.from({ length: 100 / w }, (_, i) => this.tint((i + 1) * w));
    }
    shades(weight, w = defaultNumberParam(weight, 10)) {
        return Array.from({ length: 100 / w }, (_, i) => this.shade((i + 1) * w));
    }
    all(weight = 10) {
        return [
            ...this.tints(weight).reverse(),
            Object.assign(this),
            ...this.shades(weight),
        ];
    }
    hexString() {
        return rgb2hex(this.alpha >= 1 ? this.rgb : [...this.rgb, this.alpha]);
    }
    rgbString() {
        const channels = (this.alpha >= 1 ? this.rgb : [...this.rgb, this.alpha]).join(', ');
        return `${this.alpha >= 1 ? 'rgb' : 'rgba'}(${channels})`;
    }
    getBrightness() {
        return Math.round((this.rgb.reduce((a, b) => a + b) / (255 * 3)) * 100);
    }
    _setFromRGB([r, g, b, a]) {
        [this.rgb, this.alpha] = [[r, g, b], a];
        return this;
    }
    _setFromHSL([h, s, l, a]) {
        [this.rgb, this.alpha] = [hsl2rgb([h, s, l]).map(Math.round), a];
        return this;
    }
}

const DEFAULT_WEIGHT = 5;
const valueOptions = {
    50: 1,
    100: 2,
    200: 4,
    500: 20,
    600: 22,
    700: 24,
};
function generate(color, type = '') {
    const patterns = {};
    const pColors = new ColorCore(color).all(DEFAULT_WEIGHT);
    Object.keys(valueOptions).forEach((key) => {
        patterns[`${type}${key}`] = pColors[valueOptions[key]].hexString();
    });
    return patterns;
}

function getRgbaFromHex(hex, alpha = 1) {
    const rgb = hex2Rgb(hex);
    if (!rgb) {
        return null;
    }
    return `rgba(${rgb.red}, ${rgb.green}, ${rgb.blue}, ${alpha})`;
}

const functionalColors = {
    primary: '#ee112e',
    secondary: '#0674e8',
    link: '#1990ff',
    pending: '#ffa940',
    success: '#43da71',
    error: '#da4343',
};
let presetPalettes = {};
Object.keys(functionalColors).forEach((key) => {
    presetPalettes = Object.assign(Object.assign({}, presetPalettes), generate(functionalColors[key], key));
});
const themeColor = Object.assign({ white: '#FFFFFF', tableHeader: '#FBFBFD', background: '#F8F8FC', divider: '#E4E5F0', disable: '#DCDEE9', border: '#BCBFD6', placeholder: '#A7ABC3', textSecondary: '#82869E', textPrimary: '#434657', textTitle: '#1B1D29', black: '#000000', primary50: '', primary100: '', primary200: '', primary500: '', primary600: '', primary700: '', secondary50: '', secondary100: '', secondary200: '', secondary500: '', secondary600: '', secondary700: '', link50: '', link100: '', link200: '', link500: '', link600: '', link700: '', pending50: '', pending100: '', pending200: '', pending500: '', pending600: '', pending700: '', success50: '', success100: '', success200: '', success500: '', success600: '', success700: '', error50: '', error100: '', error200: '', error500: '', error600: '', error700: '' }, presetPalettes);

const getBoxShadow = (props, shadow, shadowColor = 'black') => {
    const css = props.theme.shadowValue[shadow] || 'none';
    return css.replace(/{{color}}/gi, getRgbaFromHex(props.theme.colors[shadowColor], props.theme.shadowAlphaValue[shadow]));
};
const defaultTheme = {
    colors: themeColor,
    borderRadius: {
        s: CORE_SIZE.s4,
        m: CORE_SIZE.s8,
        l: CORE_SIZE.s12,
        xl: CORE_SIZE.s16,
    },
    borderWeight: {
        s: '0.5px',
        m: '1px',
        l: '1.5px',
    },
    opacity: {
        s: 0,
        m: 0.3,
        l: 0.5,
        xl: 0.7,
    },
    shadowValue: {
        'down-xs': '0 2px 2px {{color}}',
        'down-s': '0 2px 4px {{color}}',
        'down-m': '0 4px 8px {{color}}',
        'down-l': '0 4px 16px {{color}}, 0 8px 16px {{color}}',
        'up-s': '0 -2px 4px {{color}}',
        'up-m': '0 -4px 8px {{color}}',
        'up-l': '0px -4px 16px {{color}}, 0px -8px 16px {{color}};',
    },
    shadowAlphaValue: {
        'down-xs': 0.1,
        'down-s': 0.075,
        'down-m': 0.05,
        'down-l': 0.05,
        'up-s': 0.075,
        'up-m': 0.05,
        'up-l': 0.05,
    },
    spacing: CORE_SIZE,
    shadow: getBoxShadow,
};

export { getRgbaFromHex as a, defaultTheme as d, generate as g, themeColor as t };
